/** @jsx h */
import { h, VNode } from 'preact';
import { FooterTemplate, FooterItem, FooterTemplateLayer } from '../types';

const ALLOWED_TAGS = new Set([
  'a',
  'div',
  'span',
  'nav',
  'ul',
  'ol',
  'li',
  'p',
  'section',
  'footer',
  'header',
  'strong',
  'em'
]);

const UNSAFE_HREF_PATTERN = /^\s*(javascript|data|vbscript)\s*:/i;

const isSafeHref = (href: string): boolean => {
  if (!href) return false;
  if (UNSAFE_HREF_PATTERN.test(href)) return false;
  return true;
};

const buildItemElement = (
  layers: FooterTemplateLayer[],
  layerIndex: number,
  item: FooterItem
): VNode | null => {
  if (layerIndex >= layers.length) return null;

  const layer = layers[layerIndex];

  if (!ALLOWED_TAGS.has(layer.tag)) {
    return buildItemElement(layers, layerIndex + 1, item);
  }

  const isLast = layerIndex === layers.length - 1;
  const props: Record<string, unknown> = {};

  if (layer.className) props.className = layer.className;

  if (layer.tag === 'a') {
    if (item.id) props.id = item.id;

    if (isSafeHref(item.href)) {
      props.href = item.href;

      const isExternal =
        item.href.startsWith('http://') || item.href.startsWith('https://');

      if (isExternal) {
        props.target = '_blank';
        props.rel = 'noopener noreferrer';
      }
    }
  }

  const child = isLast
    ? item.label
    : buildItemElement(layers, layerIndex + 1, item);

  return h(layer.tag, props, child);
};

interface FooterRendererProps {
  template: FooterTemplate;
  items: FooterItem[];
}

const FooterRenderer = ({ template, items }: FooterRendererProps) => {
  if (!template?.length || !items?.length) return null;

  return items.map((item, index) => {
    const element = buildItemElement(template, 0, item);
    return element ? h(element.type, { ...element.props, key: index }) : null;
  }) as unknown as VNode;
};

export default FooterRenderer;
